/**
 * Spec 082 FR-F4 — CRUD for `medium_sources` (tag + author feeds).
 * Single-page list with inline toggle + create form. No deletion — admins
 * set `is_active = false` (per 082 out-of-scope).
 */
import { useEffect, useState } from 'react';
import { Icon } from '../../components/ui/Icon.js';
import { Button } from '../../components/ui/Button.js';
import {
  createSource,
  listSources,
  updateSource,
  type MediumSource,
  type MediumSourceType,
} from '../../api/ruleIngestion.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { Select } from '../../components/Select.js';
import { useFullBleed } from '../../lib/layout-width.js';

export function RuleSourcesPage() {
  useFullBleed();
  const toast = useToast();
  const [rows, setRows] = useState<MediumSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [newType, setNewType] = useState<MediumSourceType>('tag_feed');
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newTrust, setNewTrust] = useState('0.5');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listSources()
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  async function onCreate() {
    if (newIdentifier.trim().length === 0) {
      toast.addToast('identifier required', 'error');
      return;
    }
    setCreating(true);
    try {
      await createSource({
        type: newType,
        identifier: newIdentifier.trim(),
        trust_weight: Number.parseFloat(newTrust) || 0.5,
        notes: newNotes || null,
      });
      toast.addToast('Source created', 'success');
      setNewIdentifier('');
      setNewNotes('');
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : 'Create failed', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function onToggle(source: MediumSource) {
    try {
      await updateSource(source.id, { is_active: !source.is_active });
      toast.addToast(
        `Source ${source.is_active ? 'deactivated' : 'activated'}`,
        'success',
      );
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : 'Update failed', 'error');
    }
  }

  return (
    <div data-testid="admin-rule-sources" className="hub-page--table" data-density="compact">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Rule sources' },
        ]}
      />
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title flex items-center gap-2">
            <Icon name="bx-rss" />
            Rule sources
          </h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
            Curated Medium RSS feeds that the ingestion worker polls every 4 hours.
          </p>
        </div>
      </div>

      <div className="hub-plate space-y-3">
        <h2 className="text-sm font-semibold text-[var(--hub-color-ink)]">Add source</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <label>
            <span className="block text-[var(--hub-color-ink-muted)] mb-1">Type</span>
            <Select
              value={newType}
              onChange={(v) => setNewType(v as MediumSourceType)}
              options={[
                { value: 'tag_feed', label: 'tag_feed' },
                { value: 'author_feed', label: 'author_feed' },
              ]}
            />
          </label>
          <label className="md:col-span-2">
            <span className="block text-[var(--hub-color-ink-muted)] mb-1">Identifier</span>
            <input
              type="text"
              placeholder={newType === 'tag_feed' ? 'e.g. generative-engine-optimization' : 'e.g. username'}
              value={newIdentifier}
              onChange={(e) => setNewIdentifier(e.target.value)}
              className="hub-input w-full"
            />
          </label>
          <label>
            <span className="block text-[var(--hub-color-ink-muted)] mb-1">Trust (0-1)</span>
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={newTrust}
              onChange={(e) => setNewTrust(e.target.value)}
              className="hub-input w-full"
            />
          </label>
        </div>
        <label className="block text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Notes (optional)</span>
          <input
            type="text"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            className="hub-input w-full"
          />
        </label>
        <div>
          <Button variant="primary" onClick={onCreate} disabled={creating} data-testid="sources-create-submit">
            {creating ? 'Creating…' : 'Add source'}
          </Button>
        </div>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="hub-table" data-testid="sources-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Identifier</th>
              <th>RSS URL</th>
              <th>Last fetched</th>
              <th className="is-num">Trust</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="text-center text-[var(--hub-color-ink-muted)]">
                  Loading…
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((s) => (
                <tr key={s.id} data-testid={`source-row-${s.id}`}>
                  <td>{s.type}</td>
                  <td className="font-mono text-xs">{s.identifier}</td>
                  <td className="text-xs text-[var(--hub-color-ink-muted)] truncate max-w-xs">{s.rss_url}</td>
                  <td className="text-xs text-[var(--hub-color-ink-muted)]">
                    {s.last_fetched_at ? new Date(s.last_fetched_at).toLocaleString() : '—'}
                  </td>
                  <td className="is-num">{s.trust_weight.toFixed(2)}</td>
                  <td>
                    <button
                      onClick={() => onToggle(s)}
                      data-testid={`source-toggle-${s.id}`}
                      className={`hub-chip ${s.is_active ? 'hub-chip--ok' : ''}`}
                    >
                      {s.is_active ? 'active' : 'inactive'}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
